import React, { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  capitalize,
  monthName,
  sameDay,
  startOfDay,
  useCrmLocale,
  weekInitials,
} from "components/crm/ui/crmTime";
import useText from "hooks/useText";

// "Web calendar": 375 wide, corners 10, week starting on Monday. Today circled in orange,
// the chosen day solid orange; past days cannot be chosen.
const Calendar = ({ value, onChange, minDate = startOfDay() }) => {
  const { text } = useText("crmUi");
  const locale = useCrmLocale();
  const [view, setView] = useState(() => {
    const d = value ?? new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const today = new Date();
  const first = (view.getDay() + 6) % 7;
  const start = new Date(view.getFullYear(), view.getMonth(), 1 - first);
  const days = Array.from(
    { length: 42 },
    (_, i) =>
      new Date(start.getFullYear(), start.getMonth(), start.getDate() + i)
  );
  const rows = days[35].getMonth() === view.getMonth() ? 6 : 5;
  const move = (n) =>
    setView(new Date(view.getFullYear(), view.getMonth() + n, 1));
  const canGoBack =
    view > new Date(minDate.getFullYear(), minDate.getMonth(), 1);

  return (
    <div className="w-[375px] rounded-[10px] border border-[#aeaeae] bg-white px-6 pb-4 pt-6 shadow-pop">
      <div className="mb-4 flex items-center justify-between">
        <p className="text-[16.5px] font-medium text-ink">
          {capitalize(monthName(view.getMonth(), locale))} {view.getFullYear()}
        </p>
        <div className="flex gap-1">
          <button
            type="button"
            aria-label={text("previousMonth")}
            disabled={!canGoBack}
            onClick={() => move(-1)}
            className="rounded p-1 text-ink hover:bg-surfaceMuted disabled:opacity-30"
          >
            <ChevronLeft size={14} />
          </button>
          <button
            type="button"
            aria-label={text("nextMonth")}
            onClick={() => move(1)}
            className="rounded p-1 text-ink hover:bg-surfaceMuted"
          >
            <ChevronRight size={14} />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-7 text-center">
        {weekInitials(locale).map((d) => (
          <span key={d} className="pb-2 text-sm text-ink">
            {d}
          </span>
        ))}
        {days.slice(0, rows * 7).map((d) => {
          const out = d.getMonth() !== view.getMonth();
          const past = d < startOfDay(minDate);
          const selected = value && sameDay(d, value);
          const isToday = sameDay(d, today);
          return (
            <button
              key={d.toISOString()}
              type="button"
              disabled={past}
              onClick={() => onChange(d)}
              className={[
                "m-[2px] h-[35px] rounded-[10px] text-sm transition-colors",
                selected
                  ? "bg-primary font-medium text-white"
                  : isToday
                  ? "rounded-[5px] border-2 border-primary font-bold text-ink"
                  : "",
                !selected && !past ? "hover:bg-[#ffa43d] hover:text-white" : "",
                past
                  ? "cursor-default text-[#a7a7a7]"
                  : !selected && (out ? "text-faint" : "text-ink"),
              ].join(" ")}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default Calendar;
